import React, { useEffect } from 'react'
import ReactRouterPropTypes from 'react-router-prop-types'
import { Route, Switch, Redirect } from 'react-router-dom'

import Header from '../Header/Header'
import CartPage from '../CartPage/CartPage'
import CheckoutPage from '../CheckoutPage/CheckoutPage'
import ConfirmPage from '../ConfirmPage/ConfirmPage'
import MenuPage from '../MenuPage/MenuPage'
import AccountPage from '../AccountPage/AccountPage'
import { AddItem } from '../Modifiers/AddItem/AddItem'
import { MyOrdersPage } from '../MyOrdersPage/MyOrdersPage'
import { ModeRouter } from '../ModeRouter/ModeRouter'
import { CartProvider } from '../CartProvider/CartProvider'
import { FulfillmentProvider } from '../FulfillmentProvider/FulfillmentProvider'
import { useRestaurant } from '../RestaurantProvider/RestaurantProvider'
import { AuthenticatedRoute, AuthenticatedRouteWhenFlagOn } from '../AuthenticatedRoute/AuthenticatedRoute'
import { WrapIfFlagOn, FlagOn } from '../FeatureFlag/FeatureFlag'
import { useRestaurantStorage } from '../../utils/restaurant-storage'
import { AvailabilityProvider } from '../../utils/availability'
import { ReorderProvider } from '../Reorder/ReorderProvider/ReorderProvider'
import { GiftCardProvider } from '../GiftCardProvider/GiftCardProvider'
import { ExperimentsProvider } from '../ExperimentsProvider/ExperimentsProvider'
import { Spotlight } from '../Spotlight/Spotlight'
import { IframeTracker } from '../IframeTracker/IframeTracker'
import { UpsellsProvider } from '../Upsells/UpsellsProvider'
import { upsellSortRules, upsellFilterRules } from '../Upsells/upsell-rules'
import { TakeoutCTA } from '../TakeoutCTA/TakeoutCTA'
import { canMakeApplePayPayment } from '../ApplePay/apple-pay-utils'
import { useAnalytics } from '../AnalyticsProvider/AnalyticsProvider'

const RestaurantPage = ({ match, history, location }) => {
  const { path, url } = match
  const { state = {} } = location
  const { restaurantGuid } = useRestaurant()
  const restaurantStorage = useRestaurantStorage()
  const { addEventProperties } = useAnalytics()
  const pathBase = path === '/' ? '' : path

  const goToRestaurantHome = () => {
    if (state.internal) {
      history.go(-1)
    } else {
      history.replace(url)
    }
  }

  useEffect(() => {
    const canMakeApplePayPayments = canMakeApplePayPayment()
    addEventProperties({ canMakeApplePayPayments })
  }, [addEventProperties])

  const addItemRoute = `${pathBase}/add/:itemGuid/:itemGroupGuid`
  const WrappedModifiers = ({
    match: {
      params: { itemGuid, itemGroupGuid }
    }
  }) => (
    <AddItem
      itemGuid={itemGuid}
      itemGroupGuid={itemGroupGuid}
      onClose={goToRestaurantHome}
    />
  )
  return (
    <ExperimentsProvider>
      <AvailabilityProvider>
        <CartProvider restaurantGuid={restaurantGuid} restaurantStorage={restaurantStorage}>
          <UpsellsProvider
            filterRules={upsellFilterRules}
            sortRules={upsellSortRules}
          >
            <WrapIfFlagOn
              name='oo-v3-gift-card-redemption'
              wrapper={(children) => <GiftCardProvider>{children}</GiftCardProvider>}
              defaultValue={false}
            >
              <React.Fragment>
                <FlagOn name='oo-v3-spotlight'>
                  <Route exact path={[addItemRoute, path]} component={Spotlight} />
                </FlagOn>
                <Header />
                <IframeTracker />
                <FulfillmentProvider>
                  <ReorderProvider>
                    <React.Fragment>
                      <ModeRouter />
                      <Route path={addItemRoute} component={WrappedModifiers} />
                      <Switch>
                        <Route path={`${pathBase}/cart`} component={CartPage} />
                        <Route path={`${pathBase}/checkout`} component={CheckoutPage} />
                        <AuthenticatedRoute path={`${pathBase}/account`} component={AccountPage} />
                        <AuthenticatedRouteWhenFlagOn flag='oo-v3-reorder' path={`${pathBase}/orders`} component={MyOrdersPage} />
                        <Route
                          path={`${pathBase}/confirm/:orderGuid`}
                          component={ConfirmPage}
                        />
                        <Route exact path={[addItemRoute, path]} component={MenuPage} />
                        <Redirect to={path} />
                      </Switch>
                      <FlagOn name='oo-v3-takeout-app-cta'>
                        <TakeoutCTA />
                      </FlagOn>
                    </React.Fragment>
                  </ReorderProvider>
                </FulfillmentProvider>
              </React.Fragment>
            </WrapIfFlagOn>
          </UpsellsProvider>
        </CartProvider>
      </AvailabilityProvider>
    </ExperimentsProvider>
  )
}

RestaurantPage.propTypes = {
  match: ReactRouterPropTypes.match.isRequired,
  location: ReactRouterPropTypes.location.isRequired,
  history: ReactRouterPropTypes.history.isRequired
}

export default RestaurantPage
